import { useEffect, useState } from "react";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Select,
  Subtitle2,
  Switch,
  Tab,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TabList,
  Textarea,
} from "@fluentui/react-components";
import {
  isWeekday,
  mondayOf,
  PAY_TYPE_LABEL,
  PAY_UNIT,
  isFlatPay,
  PAY_TYPES,
  annualTargetPay,
  bonusFromSplit,
  type RoleTarget,
  standardHolidays,
  STATES,
  type PayType,
  today,
  validateHolidays,
  validateTaxTable,
  type Holiday,
  type PayrollTaxYear,
  type Role,
} from "../../../core";
import { errorText, send, useApi, type ReferenceResponse, type RoleRatesResponse } from "../api";
import { DateInput } from "../DateInput";
import { day, money, parseAmount, perHour, when } from "../format";
import { go } from "../router";
import { SharedCosts } from "./SharedCosts";
import { Seniority } from "./Clients";
import { ErrorBar, Loading, Page, useMe, Warnings } from "../ui";

const TABS = [
  { key: "defaults", label: "Planning defaults", needs: "financials.read" },
  { key: "tax", label: "Payroll tax", needs: "tax.admin" },
  { key: "holidays", label: "Holidays", needs: "people.edit" },
  { key: "roles", label: "Roles and standard rates", needs: "comp.read" },
  { key: "shared", label: "Shared costs", needs: "financials.read" },
  { key: "seniority", label: "Seniority", needs: "people.edit" },
] as const;

export function Settings({ tab }: { tab?: string }) {
  const me = useMe();
  const tabs = TABS.filter((t) => me.can(t.needs));
  const current = tabs.find((t) => t.key === tab) ?? tabs[0];
  const ref = useApi<ReferenceResponse>("/reference");
  return (
    <Page title="Settings">
      <TabList selectedValue={current?.key} onTabSelect={(_, d) => go("settings", String(d.value))}>
        {tabs.map((t) => (
          <Tab key={t.key} value={t.key}>
            {t.label}
          </Tab>
        ))}
      </TabList>
      <ErrorBar message={ref.error} />
      {!ref.data ? (
        <Loading />
      ) : current?.key === "defaults" ? (
        <Defaults data={ref.data} reload={ref.reload} />
      ) : current?.key === "tax" ? (
        <TaxTables data={ref.data} reload={ref.reload} />
      ) : current?.key === "holidays" ? (
        <Holidays data={ref.data} reload={ref.reload} />
      ) : current?.key === "roles" ? (
        <Roles data={ref.data} reload={ref.reload} />
      ) : current?.key === "shared" ? (
        <SharedCosts />
      ) : current?.key === "seniority" ? (
        <Seniority />
      ) : null}
    </Page>
  );
}

function Confirmation({ confirmed, by, at }: { confirmed: boolean; by?: string; at?: string }) {
  return confirmed ? (
    <Badge appearance="tint" color="success">
      Confirmed by {by} on {at ? when(at) : "—"}
    </Badge>
  ) : (
    <Badge appearance="tint" color="warning">
      Placeholder: not yet confirmed
    </Badge>
  );
}

function YearPicker({ years, year, onYear, onAdd }: { years: number[]; year: number; onYear: (y: number) => void; onAdd?: () => void }) {
  return (
    <div className="toolbar">
      <Select value={String(year)} onChange={(_, d) => onYear(Number(d.value))} aria-label="Year">
        {years.map((y) => (
          <option key={y}>{y}</option>
        ))}
      </Select>
      {onAdd && <Button onClick={onAdd}>Add {Math.max(...years) + 1}</Button>}
    </div>
  );
}

const pctText = (rate: number) => String(Math.round(rate * 1_000_000) / 10_000);

// --- Planning defaults (PE9, §3.6) ------------------------------------------------------------------

function Defaults({ data, reload }: { data: ReferenceResponse; reload: () => void }) {
  const me = useMe();
  const editable = me.can("people.edit");
  const s = data.settings;
  const [target, setTarget] = useState(String(Math.round(s.targetMargin * 1000) / 10));
  const [overhead, setOverhead] = useState(String(s.overheadPerHour));
  const [over, setOver] = useState(String(Math.round(s.loadThresholds.over * 100)));
  const [under, setUnder] = useState(String(Math.round(s.loadThresholds.under * 100)));
  const [cutover, setCutover] = useState(s.actualsCutover ?? "2026-11-01");
  const [reminder, setReminder] = useState(!!(s as { mondayReminder?: boolean }).mondayReminder);
  const [gap, setGap] = useState(String((s as { underReportThreshold?: number }).underReportThreshold ?? 4));
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const t = Number(target);
  const o = Number(overhead);
  const ov = Number(over);
  const un = Number(under);
  const valid = gap !== "" && Number(gap) >= 0 && Number(gap) <= 40 && target !== "" && overhead !== "" && t >= 0 && t < 100 && o >= 0 && o < 1_000 && over !== "" && under !== "" && un > 0 && un < ov && ov <= 500 && /^\d{4}-\d{2}-\d{2}$/.test(cutover) && (cutover.endsWith("-01") || mondayOf(cutover) === cutover);
  async function save() {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/reference/settings", { etag: s._etag ?? null, settings: { targetMargin: t / 100, overheadPerHour: o, loadThresholds: { over: ov / 100, under: un / 100 }, actualsCutover: cutover, mondayReminder: reminder, underReportThreshold: Number(gap) } });
      setSaved(true);
      reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }
  return (
    <Card className="panel">
      <Subtitle2>Planning defaults</Subtitle2>
      <Caption1>
        The target margin colors every project's economics and sets "price for target". A project can override it. Overhead is charged
        per planned hour in the fully burdened view only; contribution margin never includes it. Load colors (§3.9): red above the
        over-allocated level, blue below the under-allocated one, teal between.
      </Caption1>
      <div className="form-grid">
        <Field label="Target margin">
          <Input
            type="number"
            min={0}
            max={99}
            contentAfter="%"
            value={target}
            disabled={!editable}
            onChange={(_, d) => {
              setTarget(d.value);
              setSaved(false);
            }}
          />
        </Field>
        <Field label="Overhead per planned hour" hint="Used only for a year with no Shared costs budget; 0 then hides the fully burdened view.">
          <Input
            type="number"
            min={0}
            contentBefore="$"
            contentAfter="/h"
            value={overhead}
            disabled={!editable}
            onChange={(_, d) => {
              setOverhead(d.value);
              setSaved(false);
            }}
          />
        </Field>
        <Field label="Monday reminder" hint="Email each timekeeper their own gaps and uncategorized events at 08:00 Central on Mondays (Actuals → Periods previews it).">
          <Switch checked={reminder} disabled={!editable} label={reminder ? "On" : "Off"} onChange={(_, d) => (setReminder(d.checked), setSaved(false))} />
        </Field>
        <Field label="Under-reported week after" hint="A locked week this many hours short of expected goes to the inbox.">
          <Input type="number" min={0} contentAfter="h short" value={gap} disabled={!editable} onChange={(_, d) => (setGap(d.value), setSaved(false))} />
        </Field>
        <Field label="Over-allocated above" hint="Red in load and capacity views.">
          <Input
            type="number"
            min={1}
            max={500}
            contentAfter="%"
            value={over}
            disabled={!editable}
            onChange={(_, d) => {
              setOver(d.value);
              setSaved(false);
            }}
          />
        </Field>
        <Field label="Under-allocated below" hint="Blue; must be below the over-allocated level.">
          <Input
            type="number"
            min={1}
            max={500}
            contentAfter="%"
            value={under}
            disabled={!editable}
            onChange={(_, d) => {
              setUnder(d.value);
              setSaved(false);
            }}
          />
        </Field>
        <Field
          label="Calendar sync takes over"
          hint="The 1st of a month or a Monday. The Timecard Tool import covers hours before it; the calendar sync, from it on (AC4)."
          validationMessage={cutover && !cutover.endsWith("-01") && mondayOf(cutover) !== cutover ? "Pick the 1st of a month or a Monday." : undefined}
        >
          <DateInput
            type="date"
            value={cutover}
            disabled={!editable}
            onChange={(_, d) => {
              setCutover(d.value);
              setSaved(false);
            }}
          />
        </Field>
      </div>
      <ErrorBar message={error} />
      {editable && (
        <div className="toolbar">
          <Button appearance="primary" disabled={!valid || busy} onClick={() => void save()}>
            Save
          </Button>
          {saved && <Caption1>Saved.</Caption1>}
        </div>
      )}
    </Card>
  );
}

// --- Payroll tax (EC8) ----------------------------------------------------------------------------

type TaxForm = { ssRate: string; ssBase: string; medicare: string; futaRate: string; futaBase: string; note: string; suta: { state: string; rate: string; base: string }[] };

const toForm = (t: PayrollTaxYear & { note?: string }): TaxForm => ({
  ssRate: pctText(t.socialSecurity.rate),
  ssBase: String(t.socialSecurity.wageBase),
  medicare: pctText(t.medicare.rate),
  futaRate: pctText(t.futa.rate),
  futaBase: String(t.futa.wageBase),
  note: t.note ?? "",
  suta: Object.entries(t.suta).map(([state, s]) => ({ state, rate: pctText(s.rate), base: String(s.wageBase) })),
});

function TaxTables({ data, reload }: { data: ReferenceResponse; reload: () => void }) {
  const years = data.taxTables.map((t) => t.year);
  const [year, setYear] = useState(years.includes(new Date().getFullYear()) ? new Date().getFullYear() : years[0]);
  const table = data.taxTables.find((t) => t.year === year);
  const [form, setForm] = useState<TaxForm | null>(table ? toForm(table) : null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(table ? toForm(table) : null), [table]);
  if (!table || !form) return <Body1>No tax tables yet.</Body1>;

  const rate = (s: string) => parseAmount(s) / 100;
  const doc = {
    year,
    socialSecurity: { rate: rate(form.ssRate), wageBase: parseAmount(form.ssBase) },
    medicare: { rate: rate(form.medicare) },
    futa: { rate: rate(form.futaRate), wageBase: parseAmount(form.futaBase) },
    suta: Object.fromEntries(form.suta.filter((s) => s.state).map((s) => [s.state, { rate: rate(s.rate), wageBase: parseAmount(s.base) }])),
    note: form.note,
  };
  const problems = validateTaxTable(doc);
  const set = (k: Exclude<keyof TaxForm, "suta">) => (_: unknown, d: { value: string }) => setForm((f) => f && { ...f, [k]: d.value });
  const setSuta = (i: number, k: "state" | "rate" | "base", v: string) => setForm((f) => f && { ...f, suta: f.suta.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });

  async function save(confirm: boolean) {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", `/reference/tax/${year}`, { etag: table!._etag, table: doc, confirm });
      reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  async function addYear() {
    const next = Math.max(...years) + 1;
    const latest = data.taxTables.find((t) => t.year === Math.max(...years))!;
    setBusy(true);
    try {
      await send("PUT", `/reference/tax/${next}`, {
        etag: null,
        table: { ...latest, note: `Copied from ${latest.year}. Update the ${next} Social Security wage base, SUTA rates and any FUTA credit reduction.` },
      });
      setYear(next);
      reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>Employer payroll tax, {year}</Subtitle2>
        <Confirmation confirmed={table.confirmed} by={table.confirmedBy} at={table.confirmedAt} />
      </div>
      <YearPicker years={years} year={year} onYear={setYear} onAdd={() => void addYear()} />
      <Caption1>
        Rates cost every employee's hours in this year. Planning uses each person's annual average, so a rate stays stable all year.
        Any edit returns the table to "not confirmed" until it is confirmed again.
      </Caption1>
      <div className="form-grid three">
        <Field label="Social Security rate">
          <Input value={form.ssRate} onChange={set("ssRate")} contentAfter="%" inputMode="decimal" />
        </Field>
        <Field label="Social Security wage base">
          <Input value={form.ssBase} onChange={set("ssBase")} contentBefore="$" inputMode="decimal" />
        </Field>
        <Field label="Medicare rate (no wage base)">
          <Input value={form.medicare} onChange={set("medicare")} contentAfter="%" inputMode="decimal" />
        </Field>
        <Field label="FUTA rate, after the state credit">
          <Input value={form.futaRate} onChange={set("futaRate")} contentAfter="%" inputMode="decimal" />
        </Field>
        <Field label="FUTA wage base">
          <Input value={form.futaBase} onChange={set("futaBase")} contentBefore="$" inputMode="decimal" />
        </Field>
      </div>
      <Subtitle2>State unemployment (SUTA)</Subtitle2>
      <Caption1>Each state's rate is specific to 3Seven: take it from that state's annual rate notice.</Caption1>
      {form.suta.length === 0 ? (
        <Warnings items={["No state has a SUTA rate yet. Until one is added, SUTA is left out of every rate in that state."]} />
      ) : (
        <Table size="small" className="data-table narrow" aria-label="SUTA by state">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>State</TableHeaderCell>
              <TableHeaderCell>Rate</TableHeaderCell>
              <TableHeaderCell>Wage base</TableHeaderCell>
              <TableHeaderCell aria-label="Remove" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {form.suta.map((s, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Select value={s.state} onChange={(_, d) => setSuta(i, "state", d.value)} aria-label="State">
                    <option value="">Pick</option>
                    {STATES.map((st) => (
                      <option key={st}>{st}</option>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Input value={s.rate} onChange={(_, d) => setSuta(i, "rate", d.value)} contentAfter="%" aria-label={`${s.state} rate`} />
                </TableCell>
                <TableCell>
                  <Input value={s.base} onChange={(_, d) => setSuta(i, "base", d.value)} contentBefore="$" aria-label={`${s.state} wage base`} />
                </TableCell>
                <TableCell>
                  <Button size="small" appearance="subtle" onClick={() => setForm((f) => f && { ...f, suta: f.suta.filter((_, j) => j !== i) })}>
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div className="toolbar">
        <Button onClick={() => setForm((f) => f && { ...f, suta: [...f.suta, { state: "", rate: "", base: "" }] })}>Add a state</Button>
      </div>
      <Field label="Note">
        <Textarea value={form.note} onChange={(_, d) => setForm((f) => f && { ...f, note: d.value })} />
      </Field>
      {problems.length > 0 && <Warnings items={problems} />}
      <ErrorBar message={error} />
      <div className="toolbar">
        <Button disabled={busy || problems.length > 0} onClick={() => void save(false)}>
          Save
        </Button>
        <Button appearance="primary" disabled={busy || problems.length > 0} onClick={() => void save(true)}>
          Save and confirm
        </Button>
      </div>
    </Card>
  );
}

// --- Holidays (EC5) -------------------------------------------------------------------------------

function Holidays({ data, reload }: { data: ReferenceResponse; reload: () => void }) {
  const years = data.holidays.map((h) => h.year);
  const [year, setYear] = useState(years.includes(new Date().getFullYear()) ? new Date().getFullYear() : years[0]);
  const list = data.holidays.find((h) => h.year === year);
  const [days, setDays] = useState<Holiday[]>(list?.days ?? []);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  useEffect(() => setDays(list?.days ?? []), [list]);
  if (!list) return <Body1>No holiday lists yet.</Body1>;
  const problems = validateHolidays({ year, days });
  const weekdayCount = days.filter((d) => d.date && isWeekday(d.date)).length;

  async function save(confirm: boolean, etag: string | null = list!._etag, y = year, list2 = days) {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", `/reference/holidays/${y}`, { etag, days: list2, confirm });
      reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>Company holidays, {year}</Subtitle2>
        <Confirmation confirmed={list.confirmed} by={list.confirmedBy} at={list.confirmedAt} />
      </div>
      <YearPicker
        years={years}
        year={year}
        onYear={setYear}
        onAdd={() => {
          const next = Math.max(...years) + 1;
          void save(false, null, next, standardHolidays(next)).then(() => setYear(next));
        }}
      />
      <Caption1>
        {weekdayCount} of these fall on weekdays. Each weekday holiday removes a day of capacity and raises every hourly rate a little.
      </Caption1>
      <Table size="small" className="data-table narrow" aria-label="Holidays">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Date</TableHeaderCell>
            <TableHeaderCell>Holiday</TableHeaderCell>
            <TableHeaderCell aria-label="Remove" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {days.map((h, i) => (
            <TableRow key={i}>
              <TableCell>
                <DateInput type="date" value={h.date} onChange={(_, d) => setDays(days.map((x, j) => (j === i ? { ...x, date: d.value } : x)))} aria-label="Date" />
              </TableCell>
              <TableCell>
                <Input value={h.name} onChange={(_, d) => setDays(days.map((x, j) => (j === i ? { ...x, name: d.value } : x)))} aria-label="Holiday name" />
              </TableCell>
              <TableCell>
                <Button size="small" appearance="subtle" onClick={() => setDays(days.filter((_, j) => j !== i))}>
                  Remove
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="toolbar">
        <Button onClick={() => setDays([...days, { date: `${year}-01-01`, name: "" }])}>Add a holiday</Button>
        <Button appearance="subtle" onClick={() => setDays(standardHolidays(year))}>
          Reset to the standard US list
        </Button>
      </div>
      {problems.length > 0 && <Warnings items={problems} />}
      <ErrorBar message={error} />
      <div className="toolbar">
        <Button disabled={busy || problems.length > 0} onClick={() => void save(false)}>
          Save
        </Button>
        <Button appearance="primary" disabled={busy || problems.length > 0} onClick={() => void save(true)}>
          Save and confirm
        </Button>
      </div>
    </Card>
  );
}

// --- Roles and standard rates (EC7) ---------------------------------------------------------------

function Roles({ data, reload }: { data: ReferenceResponse; reload: () => void }) {
  const me = useMe();
  const editable = me.can("people.edit");
  const [asOf, setAsOf] = useState(today());
  const rates = useApi<RoleRatesResponse>(`/role-rates?asOf=${asOf}`);
  const [roles, setRoles] = useState<Partial<Role>[]>(data.roles.roles);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [overriding, setOverriding] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [targeting, setTargeting] = useState<string | null>(null);
  const [editingTarget, setEditingTarget] = useState<RoleTarget | null>(null);
  const [withdrawingTarget, setWithdrawingTarget] = useState<string | null>(null);
  useEffect(() => setRoles(data.roles.roles), [data.roles]);
  const changed = JSON.stringify(roles) !== JSON.stringify(data.roles.roles);

  async function saveRoles() {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/reference/roles", { etag: data.roles._etag, roles });
      reload();
      rates.reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }


  const rateFor = (id?: string) => rates.data?.roleRates.find((r) => r.roleId === id);
  const roleName = (id: string) => data.roles.roles.find((r) => r.id === id)?.name ?? id;
  const liveOverrides = (rates.data?.overrides ?? []).filter((o) => !o.voided).sort((a, b) => a.roleId.localeCompare(b.roleId) || b.from.localeCompare(a.from));
  const liveTargets = (rates.data?.targets ?? []).filter((t) => !t.voided).sort((a, b) => a.roleId.localeCompare(b.roleId) || b.from.localeCompare(a.from));
  const targetFor = (id?: string) => (id ? rates.data?.targetRates?.[id] : null);

  return (
    <>
      <Card className="panel wide">
        <div className="panel-head">
          <Subtitle2>Roles and standard rates</Subtitle2>
          <label className="inline-field">
            <Caption1>As of</Caption1>
            <DateInput type="date" size="small" value={asOf} onChange={(_, d) => d.value && setAsOf(d.value)} />
          </label>
        </div>
        <Caption1>
          Placeholders in a plan cost at their role's standard rate: the hours-weighted average of the role's people, unless an override is
          set. Retire a role instead of deleting it; people and plans keep pointing at it.
        </Caption1>
        <ErrorBar message={rates.error ?? error} />
        <RoleSummary rates={rates.data?.roleRates ?? []} />
        <Table size="small" className="data-table" aria-label="Roles">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Role</TableHeaderCell>
              <TableHeaderCell>Active</TableHeaderCell>
              <TableHeaderCell className="num">People</TableHeaderCell>
              <TableHeaderCell className="num">Base</TableHeaderCell>
              <TableHeaderCell className="num">OTE</TableHeaderCell>
              <TableHeaderCell>Source</TableHeaderCell>
              <TableHeaderCell className="num">Target</TableHeaderCell>
              <TableHeaderCell aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((r, i) => {
              const rate = rateFor(r.id);
              return (
                <TableRow key={r.id ?? `new-${i}`}>
                  <TableCell>
                    <Input value={r.name ?? ""} disabled={!editable} onChange={(_, d) => setRoles(roles.map((x, j) => (j === i ? { ...x, name: d.value } : x)))} aria-label="Role name" />
                  </TableCell>
                  <TableCell>
                    <Switch checked={r.active !== false} disabled={!editable} onChange={(_, d) => setRoles(roles.map((x, j) => (j === i ? { ...x, active: d.checked } : x)))} aria-label="Active" />
                  </TableCell>
                  <TableCell className="num">{rate?.people ?? "—"}</TableCell>
                  <TableCell className="num">{rate?.baseRate != null ? perHour(rate.baseRate) : "—"}</TableCell>
                  <TableCell className="num">{rate?.oteRate != null ? perHour(rate.oteRate) : "—"}</TableCell>
                  <TableCell>
                    {rate ? (rate.source === "override" ? `Override from ${day(rate.override!.from)}` : rate.source === "computed" ? "Average of people" : "None") : r.id ? "—" : "Save to compute"}
                    {rate && rate.warnings.length > 0 && (
                      <Tooltip content={rate.warnings.join(" ")} relationship="description">
                        <Badge appearance="tint" color="warning" size="small" className="in-effect">
                          {rate.people === 1 ? "Reveals one person’s pay" : "No rate yet"}
                        </Badge>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell className="num">
                    {targetFor(r.id) ? (
                      <Tooltip content={`${PAY_TYPE_LABEL[targetFor(r.id)!.type]}, burdened with the target assumptions below`} relationship="description">
                        <span>
                          {perHour(targetFor(r.id)!.rate)}
                          {targetFor(r.id)!.oteRate > targetFor(r.id)!.rate + 0.005 && <Caption1 className="block muted">OTE {perHour(targetFor(r.id)!.oteRate)}</Caption1>}
                        </span>
                      </Tooltip>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editable && r.id && (
                      <div className="toolbar">
                        <Button size="small" onClick={() => setOverriding(r.id!)}>
                          Set override…
                        </Button>
                        <Button size="small" onClick={() => setTargeting(r.id!)}>
                          Set target…
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {editable && (
          <div className="toolbar">
            <Button onClick={() => setRoles([...roles, { name: "", active: true }])}>Add a role</Button>
            {changed && (
              <Button appearance="primary" disabled={busy} onClick={() => void saveRoles()}>
                Save roles
              </Button>
            )}
          </div>
        )}
      </Card>
      <Card className="panel wide">
        <Subtitle2>Overrides</Subtitle2>
        {liveOverrides.length === 0 ? (
          <Body1 className="muted">No overrides. Every role uses the average of its people.</Body1>
        ) : (
          <Table size="small" className="data-table" aria-label="Overrides">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>From</TableHeaderCell>
                <TableHeaderCell className="num">Base</TableHeaderCell>
                <TableHeaderCell className="num">OTE</TableHeaderCell>
                <TableHeaderCell>Note</TableHeaderCell>
                <TableHeaderCell>Entered</TableHeaderCell>
                <TableHeaderCell aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {liveOverrides.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>{roleName(o.roleId)}</TableCell>
                  <TableCell>{day(o.from)}</TableCell>
                  <TableCell className="num">{perHour(o.baseRate)}</TableCell>
                  <TableCell className="num">{perHour(o.oteRate)}</TableCell>
                  <TableCell>{o.note ?? ""}</TableCell>
                  <TableCell>
                    <Caption1>
                      {o.enteredBy}, {when(o.enteredAt)}
                    </Caption1>
                  </TableCell>
                  <TableCell>
                    {editable && (
                      <Button size="small" appearance="subtle" onClick={() => setWithdrawing(o.id)}>
                        Withdraw…
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      <Card className="panel wide">
        <Subtitle2>Target rates</Subtitle2>
        <Caption1 className="block">
          What each role should cost, if it were staffed at its target. Enter a target salary (or hourly rate, or monthly fee) and type; the app burdens
          it the way it burdens a person, with the assumptions below. Projects and Performance can then cost every hour at its role's target, whoever
          fills it. Overrides above are separate and unchanged.
        </Caption1>
        {liveTargets.length === 0 ? (
          <Body1 className="muted">No targets yet. Use Set target… on a role.</Body1>
        ) : (
          <Table size="small" className="data-table" aria-label="Targets">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>From</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell className="num">Base</TableHeaderCell>
                <TableHeaderCell className="num">OTE bonus</TableHeaderCell>
                <TableHeaderCell>Note</TableHeaderCell>
                <TableHeaderCell>Entered</TableHeaderCell>
                <TableHeaderCell aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {liveTargets.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{roleName(t.roleId)}</TableCell>
                  <TableCell>{day(t.from)}</TableCell>
                  <TableCell>{PAY_TYPE_LABEL[t.type]}</TableCell>
                  <TableCell className="num">{isFlatPay(t.type) ? `$${t.amount.toLocaleString("en-US")}${PAY_UNIT[t.type]}` : perHour(t.amount)}</TableCell>
                  <TableCell className="num">{t.bonus ? `${money(t.bonus)}/yr` : <span className="muted">none</span>}</TableCell>
                  <TableCell>{t.note ?? ""}</TableCell>
                  <TableCell>
                    <Caption1>
                      {t.enteredBy}, {when(t.enteredAt)}
                    </Caption1>
                  </TableCell>
                  <TableCell>
                    {editable && (
                      <div className="toolbar">
                        <Button size="small" onClick={() => setEditingTarget(t)}>
                          Edit…
                        </Button>
                        <Button size="small" appearance="subtle" onClick={() => setWithdrawingTarget(t.id)}>
                          Withdraw…
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TargetAssumptionsForm data={data} editable={editable} onSaved={() => (reload(), rates.reload())} />
      </Card>
      {withdrawingTarget && (
        <WithdrawDialog
          what="target"
          onClose={() => setWithdrawingTarget(null)}
          onSave={async (reason) => {
            await send("POST", `/role-rates/targets/${encodeURIComponent(withdrawingTarget)}/void`, { etag: rates.data?.etag, reason });
            setWithdrawingTarget(null);
            rates.reload();
          }}
        />
      )}
      {targeting && (
        <TargetDialog
          roleName={roleName(targeting)}
          onClose={() => setTargeting(null)}
          onSave={async (t) => {
            await send("POST", "/role-rates/targets", { etag: rates.data?.etag ?? null, target: { roleId: targeting, ...t } });
            setTargeting(null);
            rates.reload();
          }}
        />
      )}
      {editingTarget && (
        <TargetDialog
          roleName={roleName(editingTarget.roleId)}
          editing={editingTarget}
          onClose={() => setEditingTarget(null)}
          onSave={async (t) => {
            await send("POST", "/role-rates/targets", { etag: rates.data?.etag ?? null, target: { roleId: editingTarget.roleId, ...t }, replaces: editingTarget.id });
            setEditingTarget(null);
            rates.reload();
          }}
        />
      )}
      {withdrawing && (
        <WithdrawDialog
          onClose={() => setWithdrawing(null)}
          onSave={async (reason) => {
            await send("POST", `/role-rates/overrides/${encodeURIComponent(withdrawing)}/void`, { etag: rates.data?.etag, reason });
            setWithdrawing(null);
            rates.reload();
          }}
        />
      )}
      {overriding && (
        <OverrideRateDialog
          roleName={roleName(overriding)}
          computed={rateFor(overriding)?.computed ?? null}
          onClose={() => setOverriding(null)}
          onSave={async (o) => {
            await send("POST", "/role-rates/overrides", { etag: rates.data?.etag ?? null, override: { roleId: overriding, ...o } });
            setOverriding(null);
            rates.reload();
          }}
        />
      )}
    </>
  );
}

function RoleSummary({ rates }: { rates: RoleRatesResponse["roleRates"] }) {
  const single = rates.filter((r) => r.source === "computed" && r.people === 1).length;
  const none = rates.filter((r) => r.source === "none").length;
  const items = [
    single && `${single === 1 ? "One role takes its rate" : `${single} roles take their rate`} from a single person, which reveals that person’s pay to anyone who later sees costs without pay visibility. Set an override for ${single === 1 ? "it" : "them"}.`,
    none && `${none === 1 ? "One role has" : `${none} roles have`} nobody with pay on file, so placeholders in ${none === 1 ? "it" : "them"} cost $0 until an override is set.`,
  ].filter(Boolean) as string[];
  return <Warnings items={items} />;
}

/** The one set of assumptions every target is burdened with (§3.12). */
function TargetAssumptionsForm({ data, editable, onSaved }: { data: ReferenceResponse; editable: boolean; onSaved: () => void }) {
  const a = data.settings.targetAssumptions;
  const [f, setF] = useState({ state: a?.state ?? "TX", benefits: String(a?.benefitsPerMonth ?? 0), hours: String(a?.hoursPerWeek ?? 45), pto: String(a?.ptoDays ?? 15) });
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const changed = !a || f.state !== a.state || Number(f.benefits) !== a.benefitsPerMonth || Number(f.hours) !== a.hoursPerWeek || Number(f.pto) !== a.ptoDays;
  async function save() {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/reference/settings", {
        etag: data.settings._etag ?? null,
        settings: { targetAssumptions: { state: f.state, benefitsPerMonth: parseAmount(f.benefits), hoursPerWeek: Number(f.hours), ptoDays: Number(f.pto) } },
      });
      onSaved();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }
  return (
    <>
      <Subtitle2>Target assumptions</Subtitle2>
      <Caption1 className="block">A target hire has no state or benefits yet, so every target uses these. Benefits apply to FTE targets only.</Caption1>
      <div className="toolbar">
        <Field label="Work state">
          <Select value={f.state} disabled={!editable} onChange={(_, d) => setF({ ...f, state: d.value })}>
            {STATES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
        </Field>
        <Field label="Employer benefits per month (FTE)">
          <Input value={f.benefits} disabled={!editable} onChange={(_, d) => setF({ ...f, benefits: d.value })} contentBefore="$" />
        </Field>
        <Field label="Capacity, h/week">
          <Input value={f.hours} disabled={!editable} onChange={(_, d) => setF({ ...f, hours: d.value })} />
        </Field>
        <Field label="PTO days a year">
          <Input value={f.pto} disabled={!editable} onChange={(_, d) => setF({ ...f, pto: d.value })} />
        </Field>
        {editable && changed && (
          <Button appearance="primary" disabled={busy} onClick={() => void save()}>
            Save assumptions
          </Button>
        )}
      </div>
      {a && a.benefitsPerMonth === 0 && <Warnings intent="info" items={["Benefits are $0, so FTE targets carry no benefits cost. Enter a typical employer cost per month."]} />}
      <ErrorBar message={error} />
    </>
  );
}

/**
 * A role's target: pay and type, and the bonus for Target OTE, entered as an amount or as an OTE split
 * (base as a share of OTE). Opened with `editing`, it replaces that target (Chris, 2026-10-15).
 */
function TargetDialog(props: {
  roleName: string;
  editing?: RoleTarget;
  onClose: () => void;
  onSave: (t: { from: string; type: PayType; amount: number; bonus: number; note?: string }) => Promise<void>;
}) {
  const e = props.editing;
  const [f, setF] = useState<{ from: string; type: PayType; amount: string; mode: "none" | "amount" | "split"; bonus: string; split: string; note: string }>({
    from: e?.from ?? today(),
    type: e?.type ?? "exempt",
    amount: e ? String(e.amount) : "",
    mode: e?.bonus ? "amount" : "none",
    bonus: e?.bonus ? String(e.bonus) : "",
    split: "80",
    note: e?.note ?? "",
  });
  const [error, setError] = useState<string>();
  const amount = parseAmount(f.amount);
  const unit = PAY_UNIT[f.type];
  const annual = Number.isFinite(amount) ? annualTargetPay({ type: f.type, amount }) : NaN;
  const split = Number(f.split);
  const bonus = f.mode === "none" ? 0 : f.mode === "amount" ? parseAmount(f.bonus) : bonusFromSplit(annual, split);
  const bonusOk = f.mode === "none" || (Number.isFinite(bonus) && bonus >= 0 && (f.mode !== "split" || (split > 0 && split <= 100)));
  const valid = !!f.from && Number.isFinite(amount) && amount > 0 && bonusOk;
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{e ? "Edit target" : "Target"}: {props.roleName}</DialogTitle>
          <DialogContent className="dialog-stack">
            <Body1>
              What this role should cost if staffed at its target. The app adds employer taxes, benefits and the productive-hours basis.
              {e ? " Saving replaces the current entry; the old one stays in the history." : ""}
            </Body1>
            <Field label="Effective from" required>
              <DateInput type="date" value={f.from} onChange={(_, d) => setF({ ...f, from: d.value })} />
            </Field>
            <Field label="Employment type" required>
              <Select value={f.type} onChange={(_, d) => setF({ ...f, type: d.value as PayType })}>
                {PAY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {PAY_TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={f.type === "exempt" ? "Target annual salary" : f.type === "partner" ? "Target annual guaranteed payments" : f.type === "contractor-monthly" ? "Target monthly fee" : "Target hourly rate"} required>
              <Input value={f.amount} onChange={(_, d) => setF({ ...f, amount: d.value })} contentBefore="$" contentAfter={unit} />
            </Field>
            <Field label="Bonus for Target OTE">
              <Select value={f.mode} onChange={(_, d) => setF({ ...f, mode: d.value as "none" | "amount" | "split" })}>
                <option value="none">No bonus: OTE equals Base</option>
                <option value="amount">An annual bonus amount</option>
                <option value="split">An OTE split: base as a share of OTE</option>
              </Select>
            </Field>
            {f.mode === "amount" && (
              <Field label="Annual bonus at target">
                <Input value={f.bonus} onChange={(_, d) => setF({ ...f, bonus: d.value })} contentBefore="$" contentAfter="/yr" />
              </Field>
            )}
            {f.mode === "split" && (
              <Field
                label="Base as a share of OTE"
                hint={Number.isFinite(bonus) && Number.isFinite(annual) ? `Bonus ${money(bonus)} a year; OTE ${money(annual + bonus)} on ${money(annual)} base` : undefined}
                validationMessage={split > 0 && split <= 100 ? undefined : "Between 1% and 100%, e.g. 80 for an 80/20 split."}
              >
                <Input value={f.split} onChange={(_, d) => setF({ ...f, split: d.value })} contentAfter="%" />
              </Field>
            )}
            <Field label="Note">
              <Input value={f.note} onChange={(_, d) => setF({ ...f, note: d.value })} placeholder="e.g. market rate for the work" />
            </Field>
            <ErrorBar message={error} />
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose}>Cancel</Button>
            <Button
              appearance="primary"
              disabled={!valid}
              onClick={() => props.onSave({ from: f.from, type: f.type, amount, bonus: f.mode === "none" ? 0 : Math.round(bonus * 100) / 100, note: f.note || undefined }).catch((err) => setError(errorText(err)))}
            >
              {e ? "Save changes" : "Set target"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function WithdrawDialog({ onClose, onSave, what = "override" }: { onClose: () => void; onSave: (reason: string) => Promise<void>; what?: "override" | "target" }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string>();
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Withdraw this {what}</DialogTitle>
          <DialogContent className="dialog-stack">
            <Body1>
              {what === "override"
                ? "The role goes back to the average of its people, or to an earlier override. The withdrawn override stays in the history."
                : "The role goes back to an earlier target, or to none: then Target costs it at Base. The withdrawn target stays in the history."}
            </Body1>
            <Field label="Reason" required>
              <Textarea value={reason} onChange={(_, d) => setReason(d.value)} />
            </Field>
            <ErrorBar message={error} />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button appearance="primary" disabled={!reason.trim()} onClick={() => onSave(reason).catch((err) => setError(errorText(err)))}>
              Withdraw
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function OverrideRateDialog(props: {
  roleName: string;
  computed: { baseRate: number; oteRate: number } | null;
  onClose: () => void;
  onSave: (o: { from: string; baseRate: number; oteRate: number; note?: string }) => Promise<void>;
}) {
  const [f, setF] = useState({ from: today(), base: props.computed ? props.computed.baseRate.toFixed(2) : "", ote: props.computed ? props.computed.oteRate.toFixed(2) : "", note: "" });
  const [error, setError] = useState<string>();
  const base = parseAmount(f.base);
  const ote = parseAmount(f.ote);
  const valid = !!f.from && Number.isFinite(base) && Number.isFinite(ote) && base >= 0 && ote >= base;
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Standard rate override: {props.roleName}</DialogTitle>
          <DialogContent className="dialog-stack">
            <Body1>
              {props.computed
                ? `The average of this role's people is ${perHour(props.computed.baseRate)} Base and ${perHour(props.computed.oteRate)} OTE.`
                : "Nobody in this role has pay on file, so there is no average to start from."}
            </Body1>
            <Field label="Effective from" required>
              <DateInput type="date" value={f.from} onChange={(_, d) => setF({ ...f, from: d.value })} />
            </Field>
            <Field label="Base rate" required>
              <Input value={f.base} onChange={(_, d) => setF({ ...f, base: d.value })} contentBefore="$" contentAfter="/h" />
            </Field>
            <Field label="OTE rate" required validationMessage={Number.isFinite(base) && Number.isFinite(ote) && ote < base ? "OTE can't be below Base." : undefined}>
              <Input value={f.ote} onChange={(_, d) => setF({ ...f, ote: d.value })} contentBefore="$" contentAfter="/h" />
            </Field>
            <Field label="Note">
              <Input value={f.note} onChange={(_, d) => setF({ ...f, note: d.value })} placeholder={`e.g. market rate for ${props.roleName.toLowerCase()}`} />
            </Field>
            <ErrorBar message={error} />
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose}>Cancel</Button>
            <Button
              appearance="primary"
              disabled={!valid}
              onClick={() => props.onSave({ from: f.from, baseRate: base, oteRate: ote, note: f.note || undefined }).catch((err) => setError(errorText(err)))}
            >
              Set override
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

